import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import Layout from 'src/layout';
import Container from '@/components/ui/container';
import TableSkeleton from '@/components/ui/tableSkeleton';
import {
    RiSearchLine,
    RiStoreLine,
    RiInboxLine,
    RiArrowDownSLine,
    RiCloseLine,
    RiCheckLine,
    RiFilterLine,
    RiBuildingLine,
    RiGlobalLine,
    RiCheckboxMultipleLine,
    RiExternalLinkLine,
    RiRefreshLine,
    RiCalendarLine,
    RiArrowLeftSLine,
    RiArrowRightSLine
} from 'react-icons/ri';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useNavigate } from 'react-router-dom';
import { getBrandOrders, getAllBrands } from '@/lib/api/brandOrdersApi';
import { toast } from 'react-toastify';

const INHOUSE_BRAND = {
    brandID: 'inhouse',
    uid: 'inhouse',
    name: 'Inhouse (Ithyaraa Direct)',
    username: 'inhouse',
    emailID: 'inhouse@ithyaraa.com',
    isInhouse: true
};

const BrandOrders = () => {
    const navigate = useNavigate();

    const [allBrands, setAllBrands] = useState([]);
    const [loadingBrands, setLoadingBrands] = useState(false);
    const [orderList, setOrderList] = useState([]);
    const [loadingAPI, setLoadingAPI] = useState(false);

    // Preset filter mode: 'except_inhouse' | 'only_inhouse' | 'all' | 'custom'
    const [filterType, setFilterType] = useState('except_inhouse');
    const [selectedBrands, setSelectedBrands] = useState([]);

    const [brandSearchText, setBrandSearchText] = useState('');
    const [showBrandDropdown, setShowBrandDropdown] = useState(false);
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        totalOrders: 0,
        hasNext: false,
        hasPrev: false
    });
    const [expandedOrders, setExpandedOrders] = useState(new Set());
    const dropdownRef = useRef(null);

    // Load all brands on mount
    useEffect(() => {
        const fetchAllBrandsList = async () => {
            try {
                setLoadingBrands(true);
                const res = await getAllBrands();
                const brandList = [INHOUSE_BRAND];
                if (res && res.success && Array.isArray(res.data)) {
                    const normalized = res.data.map(b => ({
                        ...b,
                        brandID: b.brandID || b.uid,
                        uid: b.uid || b.brandID,
                        name: b.name || b.username || 'Unnamed Brand'
                    }));
                    brandList.push(...normalized);
                }
                setAllBrands(brandList);
            } catch (err) {
                console.error('Failed to load brands:', err);
            } finally {
                setLoadingBrands(false);
            }
        };
        fetchAllBrandsList();
    }, []);

    // Click outside to close dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowBrandDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Filter brands based on search text
    const filteredBrands = useMemo(() => {
        const trimmed = brandSearchText.trim().toLowerCase();
        if (!trimmed) return allBrands;
        return allBrands.filter(b =>
            (b.name && b.name.toLowerCase().includes(trimmed)) ||
            (b.username && b.username.toLowerCase().includes(trimmed)) ||
            (b.emailID && b.emailID.toLowerCase().includes(trimmed)) ||
            (b.brandID && b.brandID.toLowerCase().includes(trimmed))
        );
    }, [allBrands, brandSearchText]);

    // Main fetch orders function
    const fetchOrders = useCallback(async ({ page = 1, overrideFilterType = null, overrideSelectedBrands = null } = {}) => {
        try {
            setLoadingAPI(true);

            const activeFilterType = overrideFilterType !== null ? overrideFilterType : filterType;
            const activeSelectedBrands = overrideSelectedBrands !== null ? overrideSelectedBrands : selectedBrands;

            let brandIDsParam = '';
            if (activeFilterType === 'custom') {
                if (activeSelectedBrands.length === 0) {
                    setOrderList([]);
                    setPagination({ currentPage: 1, totalPages: 1, totalOrders: 0, hasNext: false, hasPrev: false });
                    setLoadingAPI(false);
                    return;
                }
                brandIDsParam = activeSelectedBrands.map(b => b.brandID || b.uid).join(',');
            }

            const params = {
                filterType: activeFilterType,
                brandIDs: brandIDsParam,
                fromDate: fromDate || undefined,
                toDate: toDate || undefined,
                page,
                limit: 10
            };

            const res = await getBrandOrders(params);

            if (res && res.success) {
                setOrderList(res.data || []);
                if (res.pagination) {
                    setPagination({
                        currentPage: res.pagination.currentPage || page,
                        totalPages: res.pagination.totalPages || 1,
                        totalOrders: res.pagination.totalOrders || 0,
                        hasNext: res.pagination.hasNext || false,
                        hasPrev: res.pagination.hasPrev || false
                    });
                }
            } else {
                setOrderList([]);
                toast.error(res?.message || 'Failed to fetch brand orders');
            }
        } catch (error) {
            console.error('Error fetching brand orders:', error);
            toast.error('Server error fetching orders');
            setOrderList([]);
        } finally {
            setLoadingAPI(false);
        }
    }, [filterType, selectedBrands, fromDate, toDate]);

    useEffect(() => {
        fetchOrders({ page: 1 });
    }, [fetchOrders]);

    const handleFilterTypeChange = (newType) => {
        setFilterType(newType);
        setPagination(prev => ({ ...prev, currentPage: 1 }));
        setExpandedOrders(new Set());
        fetchOrders({ page: 1, overrideFilterType: newType });
    };

    const toggleBrandSelection = (brand) => {
        const brandID = brand.brandID || brand.uid;
        setSelectedBrands(prev => {
            const exists = prev.some(b => (b.brandID || b.uid) === brandID);
            let next;
            if (exists) {
                next = prev.filter(b => (b.brandID || b.uid) !== brandID);
            } else {
                next = [...prev, brand];
            }
            if (filterType === 'custom') {
                fetchOrders({ page: 1, overrideSelectedBrands: next });
            }
            return next;
        });
    };

    const handleRemoveBrandChip = (brandID) => {
        setSelectedBrands(prev => {
            const next = prev.filter(b => (b.brandID || b.uid) !== brandID);
            if (filterType === 'custom') {
                fetchOrders({ page: 1, overrideSelectedBrands: next });
            }
            return next;
        });
    };

    const handleSelectAllBrands = () => {
        setSelectedBrands([...allBrands]);
        if (filterType === 'custom') {
            fetchOrders({ page: 1, overrideSelectedBrands: [...allBrands] });
        }
    };

    const handleClearAllBrands = () => {
        setSelectedBrands([]);
        if (filterType === 'custom') {
            fetchOrders({ page: 1, overrideSelectedBrands: [] });
        }
    };

    const handleSearch = () => {
        setExpandedOrders(new Set());
        fetchOrders({ page: 1 });
    };

    const toggleOrderExpanded = (orderID) => {
        setExpandedOrders(prev => {
            const next = new Set(prev);
            if (next.has(orderID)) {
                next.delete(orderID);
            } else {
                next.add(orderID);
            }
            return next;
        });
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    const formatPrice = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            maximumFractionDigits: 0
        }).format(amount || 0);
    };

    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'delivered': return 'bg-emerald-50 text-emerald-700 border-emerald-200';
            case 'pending': return 'bg-amber-50 text-amber-700 border-amber-200';
            case 'processing': return 'bg-blue-50 text-blue-700 border-blue-200';
            case 'cancelled': return 'bg-rose-50 text-rose-700 border-rose-200';
            case 'returned': return 'bg-purple-50 text-purple-700 border-purple-200';
            default: return 'bg-gray-50 text-gray-700 border-gray-200';
        }
    };

    const getPaymentStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'successful':
            case 'paid': return 'bg-emerald-100 text-emerald-800 font-bold';
            case 'pending': return 'bg-amber-100 text-amber-800 font-bold';
            case 'failed': return 'bg-rose-100 text-rose-800 font-bold';
            default: return 'bg-gray-100 text-gray-800 font-bold';
        }
    };

    return (
        <Layout active="admin-brand-orders" title={'Brand Orders'}>
            <div className="p-4 md:p-6 max-w-7xl mx-auto space-y-6">

                {/* 🚀 HEADER BANNER */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-5 rounded-2xl shadow-sm border border-gray-100">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                            <RiStoreLine className="text-indigo-600" /> Brand Orders Management
                        </h1>
                        <p className="text-sm text-gray-500 mt-1">
                            Filter, monitor, and manage order fulfillments across vendor brand accounts and in-house catalog.
                        </p>
                    </div>

                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => fetchOrders({ page: pagination.currentPage })}
                            className="p-2.5 rounded-xl border border-gray-200 hover:bg-gray-50 text-gray-600 transition"
                            title="Refresh Orders"
                        >
                            <RiRefreshLine className={loadingAPI ? 'animate-spin text-indigo-600' : ''} />
                        </button>
                    </div>
                </div>

                {/* 🔍 FILTER MODE SELECTION CARD */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 space-y-5">
                    <div>
                        <label className="text-xs font-bold text-gray-500 uppercase tracking-wider block mb-3">
                            Brand Filter Mode
                        </label>
                        <div className="flex flex-wrap items-center gap-2.5">
                            {[
                                { type: 'except_inhouse', label: 'All Except Inhouse', icon: <RiFilterLine /> },
                                { type: 'only_inhouse', label: 'Only Inhouse', icon: <RiBuildingLine /> },
                                { type: 'all', label: 'All Orders (Global)', icon: <RiGlobalLine /> },
                                { type: 'custom', label: `Custom Selection ${selectedBrands.length > 0 ? `(${selectedBrands.length})` : ''}`, icon: <RiCheckboxMultipleLine /> }
                            ].map(item => (
                                <button
                                    key={item.type}
                                    type="button"
                                    onClick={() => handleFilterTypeChange(item.type)}
                                    className={`px-4 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-2 border ${
                                        filterType === item.type
                                            ? 'bg-indigo-600 text-white border-indigo-600 shadow-sm'
                                            : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                                    }`}
                                >
                                    {item.icon}
                                    {item.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Multi-Brand Custom Selector */}
                    {filterType === 'custom' && (
                        <div className="pt-4 border-t border-gray-100 space-y-3">
                            <div className="flex justify-between items-center">
                                <label className="text-xs font-bold text-gray-700">Select Vendor Brands</label>
                                <div className="flex gap-3 text-xs font-semibold">
                                    <button type="button" onClick={handleSelectAllBrands} className="text-indigo-600 hover:underline">Select All</button>
                                    <span className="text-gray-300">|</span>
                                    <button type="button" onClick={handleClearAllBrands} className="text-rose-500 hover:underline">Clear All</button>
                                </div>
                            </div>

                            {/* Chips */}
                            {selectedBrands.length > 0 && (
                                <div className="flex flex-wrap gap-1.5">
                                    {selectedBrands.map(b => (
                                        <span
                                            key={b.brandID || b.uid}
                                            className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${
                                                b.isInhouse
                                                    ? 'bg-indigo-50 text-indigo-700 border border-indigo-200'
                                                    : 'bg-blue-50 text-blue-700 border border-blue-200'
                                            }`}
                                        >
                                            {b.name}
                                            <button type="button" onClick={() => handleRemoveBrandChip(b.brandID || b.uid)} className="hover:text-rose-600">
                                                <RiCloseLine size={14} />
                                            </button>
                                        </span>
                                    ))}
                                </div>
                            )}

                            {/* Dropdown Container */}
                            <div className="relative" ref={dropdownRef}>
                                <div className="relative flex items-center">
                                    <input
                                        type="text"
                                        placeholder="Search and select brands..."
                                        value={brandSearchText}
                                        onChange={(e) => {
                                            setBrandSearchText(e.target.value);
                                            setShowBrandDropdown(true);
                                        }}
                                        onFocus={() => setShowBrandDropdown(true)}
                                        className="w-full px-4 py-2 border border-gray-200 rounded-xl text-xs font-medium focus:outline-none focus:border-indigo-500 bg-gray-50/50"
                                    />
                                    <button type="button" onClick={() => setShowBrandDropdown(prev => !prev)} className="absolute right-3 text-gray-400">
                                        <RiArrowDownSLine size={18} className={`transition-transform ${showBrandDropdown ? 'rotate-180' : ''}`} />
                                    </button>
                                </div>

                                {showBrandDropdown && (
                                    <div className="absolute z-50 w-full mt-1.5 bg-white border border-gray-100 rounded-2xl shadow-xl max-h-64 overflow-y-auto p-1">
                                        {loadingBrands ? (
                                            <div className="p-4 text-center text-gray-500 text-xs">Loading brands...</div>
                                        ) : filteredBrands.length > 0 ? (
                                            filteredBrands.map((brand, index) => {
                                                const brandID = brand.brandID || brand.uid;
                                                const isChecked = selectedBrands.some(b => (b.brandID || b.uid) === brandID);

                                                return (
                                                    <div
                                                        key={brandID || index}
                                                        onClick={() => toggleBrandSelection(brand)}
                                                        className={`px-3.5 py-2.5 rounded-xl hover:bg-indigo-50/60 cursor-pointer border-b border-gray-50 last:border-b-0 flex items-center justify-between transition-colors ${
                                                            isChecked ? 'bg-indigo-50/80 font-bold' : ''
                                                        }`}
                                                    >
                                                        <div className="flex items-center gap-2.5">
                                                            <input
                                                                type="checkbox"
                                                                checked={isChecked}
                                                                onChange={() => {}}
                                                                className="rounded text-indigo-600 focus:ring-indigo-500 h-4 w-4"
                                                            />
                                                            <div>
                                                                <div className="font-semibold text-xs text-gray-900 flex items-center gap-1.5">
                                                                    {brand.isInhouse ? (
                                                                        <RiBuildingLine className="text-indigo-600" size={14} />
                                                                    ) : (
                                                                        <RiStoreLine className="text-gray-400" size={14} />
                                                                    )}
                                                                    {brand.name}
                                                                </div>
                                                            </div>
                                                        </div>
                                                        {isChecked && <RiCheckLine className="text-indigo-600" size={16} />}
                                                    </div>
                                                );
                                            })
                                        ) : (
                                            <div className="p-4 text-center text-gray-500 text-xs">No matching brands found</div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Date Filters & Controls */}
                    <div className="flex flex-col md:flex-row gap-3 items-end pt-4 border-t border-gray-100">
                        <div className="flex-1 w-full">
                            <label className="text-xs font-semibold text-gray-600 mb-1 block">From Date</label>
                            <input
                                type="date"
                                value={fromDate}
                                onChange={(e) => setFromDate(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-200 rounded-xl text-xs font-medium focus:outline-none focus:border-indigo-500 bg-gray-50/50"
                            />
                        </div>
                        <div className="flex-1 w-full">
                            <label className="text-xs font-semibold text-gray-600 mb-1 block">To Date</label>
                            <input
                                type="date"
                                value={toDate}
                                onChange={(e) => setToDate(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-200 rounded-xl text-xs font-medium focus:outline-none focus:border-indigo-500 bg-gray-50/50"
                            />
                        </div>
                        <div className="flex gap-2 shrink-0 w-full md:w-auto">
                            {(fromDate || toDate) && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        setFromDate('');
                                        setToDate('');
                                        fetchOrders({ page: 1 });
                                    }}
                                    className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl text-xs font-semibold transition"
                                >
                                    Clear Dates
                                </button>
                            )}
                            <button
                                onClick={handleSearch}
                                className="px-6 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-bold transition shadow-sm"
                            >
                                Filter Orders
                            </button>
                        </div>
                    </div>
                </div>

                {/* 📋 ORDERS TABLE */}
                {(filterType !== 'custom' || selectedBrands.length > 0) && (
                    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                        {loadingAPI ? (
                            <TableSkeleton rows={5} />
                        ) : orderList.length === 0 ? (
                            <div className="text-center py-16 px-4">
                                <div className="w-16 h-16 rounded-full bg-indigo-50 text-indigo-500 flex items-center justify-center mx-auto mb-3">
                                    <RiInboxLine className="text-3xl" />
                                </div>
                                <h3 className="text-base font-bold text-gray-900">No Orders Found</h3>
                                <p className="text-xs text-gray-500 max-w-sm mx-auto mt-1">
                                    No orders match the selected brand and date range criteria.
                                </p>
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full text-left border-collapse">
                                    <thead>
                                        <tr className="border-b border-gray-100 bg-gray-50/50 text-[11px] font-bold text-gray-500 uppercase tracking-wider">
                                            <th className="py-3.5 px-4">Order ID</th>
                                            <th className="py-3.5 px-4">Customer</th>
                                            <th className="py-3.5 px-4">Brand(s)</th>
                                            <th className="py-3.5 px-4 text-center">Items</th>
                                            <th className="py-3.5 px-4 text-right">Amount</th>
                                            <th className="py-3.5 px-4 text-center">Status</th>
                                            <th className="py-3.5 px-4 text-center">Payment</th>
                                            <th className="py-3.5 px-4 text-center">Ordered Date</th>
                                            <th className="py-3.5 px-4 text-center">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100 text-xs font-medium text-gray-700">
                                        {orderList.map((order, idx) => {
                                            const isExpanded = expandedOrders.has(order.orderID);
                                            const brandNames = order.brandNames ? order.brandNames.split(', ') : [order.brandName || 'Inhouse'];

                                            return (
                                                <React.Fragment key={idx}>
                                                    <tr className="hover:bg-gray-50/80 transition">
                                                        {/* Order ID */}
                                                        <td className="py-3.5 px-4 font-bold">
                                                            <button
                                                                onClick={() => navigate(`/orders/details/${order.orderID}`)}
                                                                className="text-indigo-600 hover:text-indigo-800 hover:underline flex items-center gap-1 font-bold"
                                                            >
                                                                #{order.orderID} <RiExternalLinkLine className="text-xs" />
                                                            </button>
                                                        </td>

                                                        {/* Customer */}
                                                        <td className="py-3.5 px-4 font-bold text-gray-900">
                                                            {order.customerName || 'N/A'}
                                                        </td>

                                                        {/* Brands */}
                                                        <td className="py-3.5 px-4">
                                                            <div className="flex flex-wrap gap-1">
                                                                {brandNames.map((bn, bIdx) => (
                                                                    <span
                                                                        key={bIdx}
                                                                        className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                                                                            bn === 'Inhouse'
                                                                                ? 'bg-indigo-50 text-indigo-700 border border-indigo-200'
                                                                                : 'bg-blue-50 text-blue-700 border border-blue-200'
                                                                        }`}
                                                                    >
                                                                        {bn}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        </td>

                                                        {/* Items */}
                                                        <td className="py-3.5 px-4 text-center font-bold text-gray-800">
                                                            {order.itemCount}
                                                        </td>

                                                        {/* Amount */}
                                                        <td className="py-3.5 px-4 text-right font-black text-gray-900">
                                                            {formatPrice(order.brandOrderAmount)}
                                                        </td>

                                                        {/* Status */}
                                                        <td className="py-3.5 px-4 text-center">
                                                            <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold border capitalize ${getStatusColor(order.orderStatus)}`}>
                                                                {order.orderStatus || 'N/A'}
                                                            </span>
                                                        </td>

                                                        {/* Payment */}
                                                        <td className="py-3.5 px-4 text-center">
                                                            <div className="flex flex-col items-center gap-0.5">
                                                                <span className="text-[11px] font-bold text-gray-800 uppercase">{order.paymentMode || 'N/A'}</span>
                                                                <span className={`px-2 py-0.5 rounded-full text-[9px] ${getPaymentStatusColor(order.paymentStatus)}`}>
                                                                    {order.paymentStatus || 'N/A'}
                                                                </span>
                                                            </div>
                                                        </td>

                                                        {/* Date */}
                                                        <td className="py-3.5 px-4 text-center text-gray-500">
                                                            {formatDate(order.orderDate)}
                                                        </td>

                                                        {/* Actions */}
                                                        <td className="py-3.5 px-4 text-center">
                                                            <div className="flex items-center justify-center gap-2">
                                                                <button
                                                                    onClick={() => toggleOrderExpanded(order.orderID)}
                                                                    className="px-2.5 py-1 text-[11px] font-semibold text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-lg border border-indigo-100 transition"
                                                                >
                                                                    {isExpanded ? 'Hide Items' : 'View Items'}
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>

                                                    {/* EXPANDED ITEMS ROW */}
                                                    {isExpanded && order.items && order.items.length > 0 && (
                                                        <tr className="bg-indigo-50/30 border-b border-indigo-100">
                                                            <td colSpan={9} className="p-4">
                                                                <div className="bg-white rounded-xl p-3 border border-indigo-100 space-y-2">
                                                                    <div className="text-xs font-bold text-gray-800 border-b pb-2">Order Line Items ({order.items.length})</div>
                                                                    <div className="space-y-2">
                                                                        {order.items.map((item, itemIdx) => (
                                                                            <div key={itemIdx} className="flex items-center justify-between text-xs p-2 bg-gray-50 rounded-lg">
                                                                                <div className="flex items-center gap-3">
                                                                                    {item.featuredImage && (
                                                                                        <img src={typeof item.featuredImage === 'string' ? JSON.parse(item.featuredImage)?.[0]?.imgUrl : item.featuredImage?.imgUrl} alt="" className="w-8 h-8 rounded-lg object-cover" />
                                                                                    )}
                                                                                    <div>
                                                                                        <div className="font-bold text-gray-900">{item.name}</div>
                                                                                        <div className="text-[10px] text-gray-500">Brand: {item.brandName || 'Inhouse'} | Qty: {item.quantity}</div>
                                                                                    </div>
                                                                                </div>
                                                                                <div className="font-black text-gray-900">{formatPrice(item.lineTotalAfter || item.salePrice)}</div>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    )}
                                                </React.Fragment>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {/* 🔄 PAGINATION */}
                        {!loadingAPI && pagination.totalPages > 1 && (
                            <div className="flex items-center justify-between px-4 py-3 bg-gray-50/50 border-t border-gray-100 text-xs">
                                <span className="text-gray-500 font-medium">
                                    Page <strong className="text-gray-900">{pagination.currentPage}</strong> of <strong className="text-gray-900">{pagination.totalPages}</strong> ({pagination.totalOrders} total orders)
                                </span>

                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => fetchOrders({ page: pagination.currentPage - 1 })}
                                        disabled={!pagination.hasPrev}
                                        className="flex items-center gap-1 px-3 py-1.5 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-40 text-gray-700 font-semibold transition"
                                    >
                                        <RiArrowLeftSLine /> Prev
                                    </button>
                                    <button
                                        onClick={() => fetchOrders({ page: pagination.currentPage + 1 })}
                                        disabled={!pagination.hasNext}
                                        className="flex items-center gap-1 px-3 py-1.5 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-40 text-gray-700 font-semibold transition"
                                    >
                                        Next <RiArrowRightSLine />
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </Layout>
    );
};

export default BrandOrders;
